// The quotation grid's editable cell. Lifted out of QuotationDetailPage verbatim
// (ClickUp 14yj85ny3x6) so the save path can be tested without mounting a 3,500-line
// page: it is the one control every price, markup and quantity on the quotation is
// typed into, and it had no test of its own. Behaviour is unchanged apart from the
// in-flight guard documented on handleSave.
//
// This is NOT components/EditableCell.tsx. That one is the vendor-invoice/accounts cell
// with its own validate/hideActions contract; this one rounds money with ceilPrice and
// commits on blur. They are two cells and were always two cells, merging them is a
// separate job, not this ticket's.
import React, { useEffect, useRef, useState } from 'react';
import { ceilPrice } from '../../utils/priceUtils';
import { useToast } from '../../context/ToastContext';

export interface EditableCellProps {
  value: string | number;
  type?: 'text' | 'number';
  onSave: (newValue: string | number) => Promise<void>;
  className?: string;
  prefix?: string;
  suffix?: string;
  /** A money cell whose prefix is blank for now (an empty price): still rounds up like every money cell. */
  money?: boolean;
  tooltipText?: string;
  /**
   * Refuses a typed number before it is saved (B004). Gets the number as it would be stored, or
   * null when the box is blank or not a number (which `|| 0` below would otherwise turn into a
   * saved 0). Return the message to show, or null to allow. Number cells only.
   */
  validate?: (value: number | null) => string | null;
  /** When true, display value is truncated with ellipsis and full value shown in title (for narrow summary cells). */
  truncate?: boolean;
  // No `overrideDisplay`. It existed so the Markup cell could render one number and edit
  // another, the realized rate over the set rate, and that is precisely how a cell came
  // to say 30.2% about a line somebody had set to 30. A cell shows what it edits. Anything
  // else the reader needs belongs in the tooltip beside it.
}

/** Text a cell should start editing with, placeholder prompts edit as empty. */
const toEditBuffer = (value: string | number): string => {
  const val = String(value ?? '');
  return val.includes('Click to add') ? '' : val;
};

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  type = 'text',
  onSave,
  className = '',
  prefix = '',
  suffix = '',
  money = false,
  tooltipText,
  validate,
  truncate = false
}) => {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(() => toEditBuffer(value));
  const [saving, setSaving] = useState(false);
  // True once the user actually typed in this cell. A plain click-in/click-out must not
  // write anything, see the effect below for why that used to corrupt prices.
  const dirtyRef = useRef(false);
  // One save at a time, per cell. See handleSave.
  const inFlightRef = useRef(false);

  // Re-seed the edit buffer whenever the incoming value changes while we are not mid-edit.
  //
  // Without this the buffer kept its mount-time value forever (the row key is stable, so the
  // cell never remounts). The rendered text tracked the prop while the input still held the
  // original: type 7.443 into Unit Cost, ceilPrice stores 7.45, the cell reads "$7.45" but
  // clicking it opened at "7.443". Every external price change, the TTD cell rewriting USD,
  // supplier select, unit conversion, global markup, auto-adjust, did the same. Worse, blur
  // saved that stale buffer back, so merely clicking a cell and clicking away could silently
  // restore an old price.
  useEffect(() => {
    if (isEditing || saving) return;
    setEditValue(toEditBuffer(value));
    dirtyRef.current = false;
  }, [value, isEditing, saving]);

  const handleSave = async () => {
    // A save is already running: this call is the blur that the save itself caused.
    //
    // handleSave commits on blur AND on Enter, and onSave may ASK before it writes, the
    // Markup cell opens a confirmation, because a markup edit rewrites the line's selling
    // price. Both the dialog taking focus and `disabled={saving}` below blur the input while
    // that question is still on screen, and blur re-entered handleSave: the answer just given
    // was thrown away and an identical question opened on top of it. Pressing OK produced
    // another OK to press, the markup never landed, and the only button that worked was
    // Cancel (ClickUp 14yj85ny3x6).
    //
    // A ref and not the `saving` state: the re-entrant call is dispatched in the same tick
    // that queues the state update, so it would still read `false`.
    if (inFlightRef.current) return;

    // Untouched, or typed back to what it already was: close without a write.
    if (!dirtyRef.current || editValue === toEditBuffer(value)) {
      dirtyRef.current = false;
      setEditValue(toEditBuffer(value));
      setIsEditing(false);
      return;
    }

    if (type === 'number' && validate) {
      const typed = parseFloat(editValue);
      const checked = Number.isFinite(typed)
        ? money || prefix.includes('$') ? ceilPrice(typed) : Math.round(typed * 100) / 100
        : null;
      const problem = validate(checked);
      if (problem) {
        toast.error(problem);
        dirtyRef.current = false;
        setEditValue(toEditBuffer(value));
        setIsEditing(false);
        return;
      }
    }

    inFlightRef.current = true;
    setSaving(true);
    try {
      let newValue: string | number = editValue;
      if (type === 'number') {
        const numValue = parseFloat(editValue) || 0;
        // Apply proper rounding for all numeric fields to avoid floating point errors
        newValue = Math.round(numValue * 100) / 100;
        // Apply ceiling for money fields, any currency prefix, not just plain "$",
        // so the TT$ cells round the same way the USD ones do.
        if (money || prefix.includes('$')) {
          newValue = ceilPrice(numValue);
        }
      }
      await onSave(newValue);
      dirtyRef.current = false;
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      // A fixed sentence, never the caught error's own message (invariant 6) —
      // this cell saves whatever field it was given, so there is no single
      // envelope `code` to map here the way a specific handler can.
      toast.error('Failed to save changes. Please try again.');
      dirtyRef.current = false;
      setEditValue(toEditBuffer(value)); // Reset on error
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  const handleChange = (next: string) => {
    dirtyRef.current = true;
    setEditValue(next);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      dirtyRef.current = false;
      setEditValue(toEditBuffer(value));
      setIsEditing(false);
    }
  };

  const displayValue = `${prefix}${value}${suffix}`;

  if (isEditing) {
    if (type === 'text') {
      return (
        <textarea
          value={editValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyPress}
          className={`w-full px-1 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none whitespace-normal min-h-[24px] ${className}`}
          autoFocus
          disabled={saving}
          rows={3}
        />
      );
    }

    return (
      <input
        type={type}
        value={editValue}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyPress}
        className={`w-full px-1 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
        autoFocus
        disabled={saving}
      />
    );
  }

  const isPlaceholder = displayValue.includes('Click to add');

  return (
    <div
      onClick={() => setIsEditing(true)}
      className={`cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded transition-colors relative leading-relaxed flex items-center min-h-[24px] group min-w-0 overflow-hidden ${className} ${saving ? 'opacity-50' : ''}`}
      title={displayValue}
    >
      <span className={`w-full min-w-0 truncate ${isPlaceholder ? 'text-gray-400 italic' : ''}`}>
        {displayValue}
      </span>
      {tooltipText && (
        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
          {tooltipText}
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
        </span>
      )}
      {saving && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-75 rounded">
          <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default EditableCell;
